import {
  Activity,
  Award,
  CheckCircle2,
  ChevronRight,
  Clock,
  Filter,
  Loader2,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  X,
  XCircle,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ROUTES } from '../../constants/routes';
import { useBeltProfiles } from '../../hooks/belts/useBelts';
import { useCompanySports } from '../../hooks/useCompanySports';
import {
  useDismissNotification,
  usePendingNotifications,
  usePromoteFromNotification,
  usePromoteStudentManually,
  useRunEligibilityScan,
  type PendingNotificationRow,
  type ScanResult,
} from '../../hooks/belts/useEligibilityNotifications';
import { useCurrentCompany } from '../../hooks/useCurrentCompany';
import { supabase } from '../../lib/supabase';

/**
 * Promotion workflow (#749).
 *
 * Pending notifications drive the main table — promote / dismiss inline.
 * "Re-scan now" hits the scan RPC and toasts a summary. "Manual promote"
 * opens a dialog for instructor-initiated promotions outside the
 * scan/notification flow.
 */
export default function BeltPromotionsPage() {
  const notifications = usePendingNotifications();
  const sports = useCompanySports();
  const promote = usePromoteFromNotification();
  const dismiss = useDismissNotification();
  const scan = useRunEligibilityScan();

  const [sportFilter, setSportFilter] = useState<string>(''); // '' = all
  const [actionError, setActionError] = useState<string | null>(null);
  const [scanSummary, setScanSummary] = useState<ScanResult | null>(null);
  const [manualOpen, setManualOpen] = useState(false);

  const visible = useMemo(() => {
    const rows = notifications.data ?? [];
    if (!sportFilter) return rows;
    return rows.filter((n) => {
      // Match by sport name (since rows are hydrated client-side).
      const sport = sports.data?.find((s) => s.id === sportFilter);
      return n.sport_name === sport?.sport_name;
    });
  }, [notifications.data, sportFilter, sports.data]);

  const onPromote = async (n: PendingNotificationRow, notes?: string) => {
    setActionError(null);
    try {
      await promote.mutateAsync({ notificationId: n.id, notes });
    } catch (e) {
      setActionError((e as Error).message);
    }
  };

  const onDismiss = async (n: PendingNotificationRow) => {
    setActionError(null);
    try {
      await dismiss.mutateAsync({ notificationId: n.id });
    } catch (e) {
      setActionError((e as Error).message);
    }
  };

  const onRescan = async () => {
    setActionError(null);
    setScanSummary(null);
    try {
      const result = await scan.mutateAsync();
      setScanSummary(result);
    } catch (e) {
      setActionError((e as Error).message);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#EBFFEE] flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-emerald-400" />
            Promotions
          </h1>
          <p className="text-sm text-white/60 mt-1">
            Review students the scan job flagged as eligible for promotion.
            Advisory only &mdash; promote or dismiss at your discretion.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRescan}
            disabled={scan.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-white/5 hover:bg-white/10 disabled:opacity-40 text-white/80 px-3 py-1.5 text-sm border border-white/10"
          >
            {scan.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Re-scan now
          </button>
          <button
            type="button"
            onClick={() => setManualOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 px-3 py-1.5 text-sm font-medium border border-emerald-500/30"
          >
            <Award className="w-4 h-4" /> Manual promote
          </button>
        </div>
      </div>

      {scanSummary && (
        <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm p-3 flex items-center gap-2">
          <Activity className="w-4 h-4" />
          Scan finished. {scanSummary.scanned} active rank{scanSummary.scanned === 1 ? '' : 's'} scanned ·{' '}
          {scanSummary.notified_time + scanSummary.notified_attendance + scanSummary.notified_both} new notification
          {scanSummary.notified_time + scanSummary.notified_attendance + scanSummary.notified_both === 1 ? '' : 's'}
          {' '}({scanSummary.notified_time} time / {scanSummary.notified_attendance} attendance / {scanSummary.notified_both} both).
        </div>
      )}

      {actionError && (
        <div className="rounded-md bg-red-500/10 border border-red-500/30 text-red-300 text-sm p-3">
          {actionError}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-white/40" />
        <select
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className="rounded-md bg-white/5 border border-white/10 px-2 py-1 text-sm text-[#EBFFEE]"
        >
          <option value="">All disciplines</option>
          {(sports.data ?? []).map((s) => (
            <option key={s.id} value={s.id}>
              {s.sport_name}
            </option>
          ))}
        </select>
      </div>

      {notifications.isLoading && (
        <div className="flex items-center gap-2 text-white/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading notifications…
        </div>
      )}

      {!notifications.isLoading && visible.length === 0 && (
        <div className="text-sm text-white/50 rounded-md border border-white/10 bg-white/[0.02] p-6 text-center">
          No pending notifications.
          {sportFilter ? ' Try clearing the discipline filter.' : ' Run a scan or wait for the daily 06:00 UTC job.'}
        </div>
      )}

      {visible.length > 0 && (
        <div className="rounded-lg border border-white/10 overflow-hidden">
          {visible.map((n) => (
            <NotificationRow
              key={n.id}
              row={n}
              onPromote={(notes) => onPromote(n, notes)}
              onDismiss={() => onDismiss(n)}
              isPending={promote.isPending || dismiss.isPending}
            />
          ))}
        </div>
      )}

      {manualOpen && <ManualPromoteDialog onClose={() => setManualOpen(false)} />}
    </div>
  );
}

function NotificationRow({
  row,
  onPromote,
  onDismiss,
  isPending,
}: {
  row: PendingNotificationRow;
  onPromote: (notes?: string) => void;
  onDismiss: () => void;
  isPending: boolean;
}) {
  const [showNotes, setShowNotes] = useState(false);
  const [notes, setNotes] = useState('');

  const daysAtRank = Math.floor(
    (Date.now() - new Date(row.achieved_at).getTime()) / 86400000
  ) - Math.floor(row.accumulated_time_on_hold_seconds / 86400);

  const triggerBadge =
    row.triggered_by === 'time'
      ? 'bg-blue-500/10 border-blue-500/30 text-blue-300'
      : row.triggered_by === 'attendance'
        ? 'bg-purple-500/10 border-purple-500/30 text-purple-300'
        : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300';

  return (
    <div className="border-t border-white/5 first:border-t-0 px-4 py-3 hover:bg-white/[0.02]">
      <div className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <Link
              to={ROUTES.STUDENT_BELT_RANKS.replace(':studentId', row.student_id)}
              className="font-medium text-[#EBFFEE] hover:underline"
            >
              {row.student_name ?? row.student_id}
            </Link>
            <span className={`text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5 border ${triggerBadge}`}>
              {row.triggered_by}
            </span>
            <span className="text-xs text-white/40">·</span>
            <span className="text-xs text-white/60">{row.sport_name}</span>
          </div>
          <div className="text-xs text-white/60 mt-1 flex items-center gap-2 flex-wrap">
            <span className="text-white/70">{row.current_rank_name}</span>
            <ChevronRight className="w-3 h-3 text-white/40" />
            <span className="text-emerald-300">{row.eligible_rank_name}</span>
            <span className="text-white/30">·</span>
            <Clock className="w-3 h-3 text-white/40" />
            <span>{daysAtRank}d at rank</span>
            {row.profile_name && (
              <>
                <span className="text-white/30">·</span>
                <span className="text-white/40">{row.profile_name}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setShowNotes((s) => !s)}
            className="text-xs text-white/40 hover:text-white/80 px-2 py-1"
            title="Add note before action"
          >
            note…
          </button>
          <button
            type="button"
            onClick={onDismiss}
            disabled={isPending}
            className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white px-2 py-1 disabled:opacity-40"
          >
            <XCircle className="w-4 h-4" /> Dismiss
          </button>
          <button
            type="button"
            onClick={() => onPromote(notes.trim() || undefined)}
            disabled={isPending}
            className="inline-flex items-center gap-1 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black px-3 py-1.5 text-sm font-medium"
          >
            {isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Promote
          </button>
        </div>
      </div>
      {showNotes && (
        <div className="mt-2">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional note recorded on the promotion log…"
            rows={2}
            className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-emerald-500/50 resize-none"
          />
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Manual promote dialog
// ============================================================================

function ManualPromoteDialog({ onClose }: { onClose: () => void }) {
  const { data: companyId } = useCurrentCompany();
  const promoteManual = usePromoteStudentManually();

  const [studentRankId, setStudentRankId] = useState('');
  const [targetRankId, setTargetRankId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Load all active student_ranks in the company so the picker can show
  // "Student name — current rank (discipline)".
  const candidates = useManualPromoteCandidates(companyId);

  const selectedCandidate = candidates.data?.find((c) => c.student_rank_id === studentRankId);
  const profileRanks = useProfileRanksOnly(selectedCandidate?.profile_id);

  const submit = async () => {
    setError(null);
    if (!studentRankId || !targetRankId) {
      setError('Pick a student and a target rank.');
      return;
    }
    try {
      await promoteManual.mutateAsync({
        studentRankId,
        targetRankId,
        notes: notes.trim() || null,
      });
      onClose();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0f1612] border border-white/10 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-[#EBFFEE]">Manual promote</h3>
            <p className="text-xs text-white/60 mt-1">
              Promote any student to any rank in their current profile. No notification needed.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-white/50">Student / discipline</label>
          {candidates.isLoading ? (
            <div className="text-sm text-white/50">Loading…</div>
          ) : (
            <select
              value={studentRankId}
              onChange={(e) => {
                setStudentRankId(e.target.value);
                setTargetRankId('');
              }}
              className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-emerald-500/50"
            >
              <option value="">Pick…</option>
              {(candidates.data ?? []).map((c) => (
                <option key={c.student_rank_id} value={c.student_rank_id}>
                  {c.student_name ?? c.student_id} — {c.current_rank_name} ({c.sport_name})
                </option>
              ))}
            </select>
          )}
        </div>

        {selectedCandidate && (
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-wide text-white/50">Target rank</label>
            <select
              value={targetRankId}
              onChange={(e) => setTargetRankId(e.target.value)}
              className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-emerald-500/50"
            >
              <option value="">Pick…</option>
              {(profileRanks.data ?? [])
                .filter((r) => r.id !== selectedCandidate.current_rank_id)
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
            </select>
          </div>
        )}

        <div className="space-y-1">
          <label className="text-xs uppercase tracking-wide text-white/50">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded-md bg-white/5 border border-white/10 px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-emerald-500/50 resize-none"
          />
        </div>

        {error && (
          <div className="text-xs text-red-300 bg-red-500/10 border border-red-500/30 rounded-md p-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-white/70 hover:text-white">
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={promoteManual.isPending || !studentRankId || !targetRankId}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black px-3 py-1.5 text-sm font-medium"
          >
            {promoteManual.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            <ShieldCheck className="w-4 h-4" /> Promote
          </button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Inline data hooks used only by the manual-promote dialog. Kept here to
// avoid spreading single-consumer hooks across files; promote to a shared
// hooks file if a second consumer shows up.

interface ManualPromoteCandidate {
  student_rank_id: string;
  student_id: string;
  student_name?: string;
  current_rank_id: string;
  current_rank_name?: string;
  profile_id?: string;
  sport_name?: string;
}

function useManualPromoteCandidates(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ['manual-promote-candidates', companyId ?? 'none'],
    queryFn: async (): Promise<ManualPromoteCandidate[]> => {
      if (!companyId) return [];
      const { data: rows, error } = await supabase
        .from('student_ranks')
        .select('id, student_id, current_rank_id, sport_id')
        .eq('company_id', companyId)
        .eq('state', 'active');
      if (error) throw error;
      const list = rows ?? [];
      if (list.length === 0) return [];

      const studentIds = Array.from(new Set(list.map((r) => r.student_id)));
      const rankIds = Array.from(new Set(list.map((r) => r.current_rank_id)));
      const sportIds = Array.from(new Set(list.map((r) => r.sport_id)));

      const [studentsRes, ranksRes, sportsRes] = await Promise.all([
        supabase.from('user_profiles').select('id, first_name, last_name').in('id', studentIds),
        supabase.from('belt_ranks').select('id, name, profile_id').in('id', rankIds),
        supabase.from('company_sports').select('id, sport_name').in('id', sportIds),
      ]);
      if (studentsRes.error) throw studentsRes.error;
      if (ranksRes.error) throw ranksRes.error;
      if (sportsRes.error) throw sportsRes.error;

      const studentById = new Map((studentsRes.data ?? []).map((s) => [s.id, s]));
      const rankById = new Map((ranksRes.data ?? []).map((r) => [r.id, r]));
      const sportById = new Map((sportsRes.data ?? []).map((s) => [s.id, s]));

      return list.map((r) => {
        const student = studentById.get(r.student_id);
        const rank = rankById.get(r.current_rank_id);
        return {
          student_rank_id: r.id,
          student_id: r.student_id,
          student_name: student
            ? `${student.first_name ?? ''} ${student.last_name ?? ''}`.trim() || undefined
            : undefined,
          current_rank_id: r.current_rank_id,
          current_rank_name: rank?.name,
          profile_id: rank?.profile_id,
          sport_name: sportById.get(r.sport_id)?.sport_name,
        };
      });
    },
    enabled: !!companyId,
    staleTime: 30_000,
  });
}

function useProfileRanksOnly(profileId: string | undefined) {
  return useQuery({
    queryKey: ['belt-ranks-for-profile-promote', profileId ?? 'none'],
    queryFn: async () => {
      if (!profileId) return [];
      const { data, error } = await supabase
        .from('belt_ranks')
        .select('id, name, rank_order')
        .eq('profile_id', profileId)
        .order('rank_order', { ascending: true });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!profileId,
    staleTime: 60_000,
  });
}
